// Studio → Applications: the active company's applications, from the application catalog adapter.
//
// Each card states plainly:
//   • Public vs Login required — and whether that login is the one the viewer already holds (a super-admin on the
//     single Movorra login) or a separate ride-share login (a Studio-pool session).
//   • Available vs Not configured in THIS environment. An unconfigured application renders NO link.
//   • Whether the application's conversations are recorded in Studio (only the Studio-flow booking page is;
//     the agents-service chat is not), so nobody reads an empty Leads screen as "no customers".
//
// Nothing here knows about a specific company: it renders whatever definitions it is given.
import React, { useCallback, useState } from 'react';
import { Card, PageHeader, Button, useToast } from '../ui/primitives';
import { IconNavigate, IconRoute, IconCar, IconSparkles, IconLock, IconArrowRight, IconBuilding } from '../ui/icons';

const ICONS = { navigate: IconNavigate, route: IconRoute, car: IconCar, sparkles: IconSparkles, building: IconBuilding };

export function absoluteUrl(origin, path) {
  return String(origin || '').replace(/\/+$/, '') + String(path || '');
}

function Badge({ tone = 'neutral', children }) {
  const cls = tone === 'public'
    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : 'bg-surface text-body/65 border-border';
  return (
    <span className={`px-2 py-0.5 rounded-full border text-11 font-medium inline-flex items-center gap-1 ${cls}`}>
      {children}
    </span>
  );
}

function ApplicationCard({ app, origin, onCopy }) {
  const Icon = ICONS[app.icon] || IconSparkles;
  const configured = !!(app.status && app.status.configured);
  const isPublic = !!(app.access && app.access.public);
  const url = absoluteUrl(origin, app.route);

  return (
    <Card className="p-5 flex flex-col" data-testid={`app-${app.appId}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="w-10 h-10 rounded-xl bg-primary/5 text-primary flex items-center justify-center shrink-0">
          <Icon size={19} />
        </div>
        <div className="flex flex-wrap justify-end gap-1">
          {!configured && <Badge>Not configured</Badge>}
          {configured && (isPublic ? <Badge tone="public">Public</Badge> : <Badge><IconLock size={11} /> Login required</Badge>)}
        </div>
      </div>

      <h3 className="text-sm font-semibold text-body mb-1">{app.displayName}</h3>
      <p className="text-13 text-body/60 leading-relaxed mb-2 flex-1">{app.description}</p>
      <p className="text-11 text-body/50 mb-3">
        {app.records && app.records.studio ? 'Recorded in Studio Leads & Analytics' : 'Not recorded in Studio'}
      </p>

      {configured ? (
        <>
          <div className="text-11 font-mono text-body/45 truncate mb-3" title={url}>{app.route}</div>
          <div className="flex items-center gap-2">
            {/* A normal link: the destination runs its OWN login and gating. No token is ever passed. */}
            <a
              href={app.route}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-btn border border-primary/25 text-primary
                text-13 font-medium hover:bg-primary/5 transition-colors"
            >
              Open Application <IconArrowRight size={13} />
            </a>
            <Button size="sm" variant="ghost" onClick={() => onCopy(url, app.displayName)}>Copy Link</Button>
          </div>
        </>
      ) : (
        <p className="text-13 text-body/55 leading-relaxed bg-surface border border-border rounded-card px-3 py-2">
          {(app.status && app.status.reason) || 'Not set up in this environment yet.'}
        </p>
      )}
    </Card>
  );
}

function CompanyApplications({ applications, companyName, emptyMessage, singleLogin = false }) {
  const toast = useToast();
  const [origin] = useState(() => {
    try { return window.location.origin; } catch (e) { return ''; }
  });

  const copy = useCallback((url, name) => {
    try {
      navigator.clipboard.writeText(url);
      toast({ tone: 'success', message: `${name} link copied` });
    } catch (e) {
      toast({ tone: 'warning', message: 'Could not copy — select and copy the link manually.' });
    }
  }, [toast]);

  const needsLogin = applications.some((a) => !(a.access && a.access.public));

  return (
    <>
      <PageHeader title="Applications" subtitle={`The Movorra applications available to ${companyName}.`} />
      {applications.length === 0 ? (
        <Card className="p-6 text-13 text-body/60">
          {emptyMessage || 'No applications are configured for this company yet.'}
        </Card>
      ) : (
        <>
          {needsLogin && (
            <p className="text-13 text-body/60 mt-3">
              {singleLogin
                ? 'You are signed in with your Movorra login, so these applications open without signing in again.'
                : 'Login-required applications use the company (ride-share) login, which is separate from this Studio login.'}
            </p>
          )}
          <div className="grid gap-4 sm:grid-cols-2 mt-4">
            {applications.map((a) => (
              <ApplicationCard key={a.appId} app={a} origin={origin} onCopy={copy} />
            ))}
          </div>
        </>
      )}
    </>
  );
}

export default CompanyApplications;
